import { isPlainObject } from '~/utils/isPlainObject'

/**
 * Structural equality for one field's value. Arrays and plain objects recurse
 * so an in-place mutation is detected even though the reference is unchanged.
 * Everything else -- `File`, `Date`, `null`, primitives, class instances --
 * compares by `!==`: those types are not safe to walk field-by-field, and a
 * `File`/`Date` in particular carry identity or timing state a structural
 * walk would get wrong.
 */
export const valuesEqual = (previous: unknown, current: unknown): boolean => {
  // Object.is, not ===: NaN === NaN is false, which would report an untouched
  // NaN field as edited on every store event and clear its error forever.
  if (Object.is(previous, current)) return true

  if (Array.isArray(previous) && Array.isArray(current)) {
    if (previous.length !== current.length) return false
    return previous.every((item, index) => valuesEqual(item, current[index]))
  }

  if (isPlainObject(previous) && isPlainObject(current)) {
    const previousKeys = Object.keys(previous)
    if (previousKeys.length !== Object.keys(current).length) return false
    return previousKeys.every(
      (key) => Object.hasOwn(current, key) && valuesEqual(previous[key], current[key])
    )
  }

  return false
}
